import type { Prospect } from "@/lib/types";
import type { ProspectUpdate } from "@/lib/review";

/**
 * Client HTTP du backend Aurora (Slim 4) — remplace l'accès Supabase direct (Epic #43, Phase 2).
 *
 * **Même origine que la SPA** : en prod, Traefik route `/api/*` vers le conteneur backend ; en dev
 * (`npm run dev`), le proxy Vite renvoie `/api` vers le backend local (cf. `vite.config.ts`). Les
 * cookies (session HttpOnly + `csrf_token` lisible par le JS) sont donc **first-party** → pas de CORS,
 * `SameSite=Strict` opérant.
 *
 * - **Session** : cookie HttpOnly posé par le backend après connexion (login admin par mot de passe).
 * - **CSRF** : double-submit — sur les requêtes mutatives, on relit le cookie `csrf_token` et on le
 *   renvoie dans l'en-tête `X-CSRF-Token` ; le backend exige la concordance cookie ↔ en-tête.
 */

// `||` (et non `??`) : le build Docker pose `ENV VITE_API_BASE_URL=` (vide) quand l'arg n'est pas
// fourni → Vite inline une CHAÎNE VIDE, que `??` ne rattraperait pas. `||` retombe bien sur "/api".
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api";
const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Erreur HTTP du backend, portant le code de statut et le corps décodé (si présent). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {};

  let body: string | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  // Double-submit CSRF : relayer le cookie dans l'en-tête sur les requêtes mutatives. Le cookie est
  // posé par le backend sur la première requête sûre (GET) du groupe protégé (ex. /auth/me).
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers[CSRF_HEADER] = token;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body,
    credentials: "include",
  });

  if (res.status === 204) return undefined as T;

  const payload = await parseBody(res);

  if (!res.ok) {
    const message =
      (isRecord(payload) && typeof payload.error === "string" && payload.error) ||
      `Erreur ${res.status}`;
    throw new ApiError(res.status, message, payload);
  }

  return payload as T;
}

// === Auth (admin unique par mot de passe) ===

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
}

/**
 * Connexion admin : email + mot de passe → ouvre la session, renvoie l'utilisateur. `401` si
 * identifiants invalides, `423` si verrouillé (anti-brute-force).
 */
export function login(email: string, password: string): Promise<AuthUser> {
  return apiFetch<AuthUser>("/auth/login", { method: "POST", body: { email, password } });
}

export function logout(): Promise<void> {
  return apiFetch<void>("/auth/logout", { method: "POST" });
}

/** Session courante, ou `null` si non authentifié (401). */
export async function getMe(): Promise<AuthUser | null> {
  try {
    return await apiFetch<AuthUser>("/auth/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

// === Prospects (pipeline de review) ===

export function fetchProspects(): Promise<Prospect[]> {
  return apiFetch<Prospect[]>("/prospects");
}

/** Détail d'un prospect, ou `null` si introuvable (404). */
export async function fetchProspect(id: string): Promise<Prospect | null> {
  try {
    return await apiFetch<Prospect>(`/prospects/${encodeURIComponent(id)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function updateProspect(id: string, update: ProspectUpdate): Promise<Prospect> {
  return apiFetch<Prospect>(`/prospects/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: update,
  });
}
